import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'

pdfMake.vfs = pdfFonts.pdfMake.vfs
pdfMake.fonts = {
  THSarabunNew: {
    normal: 'THSarabunNew-Regular.ttf',
    bold: 'THSarabunNew-Bold.ttf',
    italics: 'THSarabunNew-Italic.ttf',
    bolditalics: 'THSarabunNew-BoldItalic.ttf'
  },
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf'
  }
}

const BANK_CODES = ['AY', 'KK', 'TTB', 'CIMB', 'EAST', 'OALT', 'KL', 'TSCO', 'NLTH', 'AEON']

function v(x) { return (x === null || x === undefined || x === '') ? '-' : String(x) }
function baht(x) {
  const n = parseFloat(x)
  if (isNaN(n)) return '-'
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

// Build the customer view object from a joined profile_credit row (used by the list view)
export function customerFromProfileRow(p) {
  return {
    customer_name: p.customer_name,
    customer_lastname: p.customer_lastname,
    customer_mobile: p.customer_mobile,
    customer_age: p.customer_age,
    customer_address: p.customer_address,
    customer_gender: p.customer_gender,
    customer_housing: p.customer_housing,
    customer_housing_other: p.customer_housing_other
  }
}

// a label/value row (2 pairs per row). Pass '' for an empty right pair.
function kv(l1, v1, l2, v2) {
  return [
    { text: l1, style: 'lbl' },
    { text: v1, style: 'val' },
    { text: l2 || '', style: 'lbl' },
    { text: v2 || '', style: 'val' }
  ]
}
// a full-width value row
function kvFull(l1, v1) {
  return [
    { text: l1, style: 'lbl' },
    { text: v1, style: 'val' },
    '', ''
  ]
}
// section block: title spanning 4 cols + the kv rows
function section(title, rows) {
  return {
    table: {
      widths: ['17%', '33%', '17%', '33%'],
      body: [
        [{ text: title, style: 'secTitle', colSpan: 4, alignment: 'left' }, '', '', ''],
        ...rows
      ]
    },
    margin: [0, 0, 0, 3],
    layout: {
      hLineColor: () => '#bbbbbb',
      vLineColor: () => '#bbbbbb',
      paddingTop: () => 1,
      paddingBottom: () => 1
    }
  }
}

export function printProfileCreditPdf(profile, customer) {
  const p = profile || {}
  const c = customer || {}
  const obligations = p.obligations_json || []
  const history = p.history_json || []
  const banks = p.banks_json || {}
  const followups = p.followups_json || []

  const custName = [c.customer_name, c.customer_lastname].filter(Boolean).join(' ') || '-'

  // income proof
  const incomeProof = [
    p.has_slip ? 'สลิป' : '',
    p.has_certificate ? 'ใบรับรอง' : '',
    p.income_cash ? 'เงินสด' : ''
  ].filter(Boolean).join(', ') || '-'

  // existing loans
  const loans = []
  if (p.cred_home) loans.push('บ้าน')
  if (p.cred_motorcycle) loans.push('มอเตอร์ไซค์')
  if (p.cred_car) loans.push('รถยนต์')
  if (p.cred_credit_card) loans.push('บัตรเครดิต')
  if (p.cred_personal_loan) loans.push('เงินกู้')
  if (p.cred_refinance) loans.push('รีไฟแนนซ์')
  if (p.cred_other) loans.push('อื่นๆ' + (p.cred_other_note ? ':' + p.cred_other_note : ''))

  // bank checklist (single line)
  const bankLines = BANK_CODES
    .filter(code => banks[code] && banks[code].on)
    .map(code => code + (banks[code].note ? '(' + banks[code].note + ')' : ''))
  const bankText = bankLines.length ? bankLines.join(', ') : '-'

  // obligations table (4 rows + total)
  const obligBody = [
    [{ text: '#', style: 'th' }, { text: 'ไฟแนนซ์', style: 'th' }, { text: 'ค่างวด', style: 'th' }, { text: 'ผ่อนมาแล้ว', style: 'th' }]
  ]
  for (let i = 0; i < 4; i++) {
    const o = obligations[i] || {}
    obligBody.push([
      { text: String(i + 1), style: 'td' },
      { text: v(o.name), style: 'td' },
      { text: baht(o.installment), style: 'td' },
      { text: v(o.paidSoFar), style: 'td' }
    ])
  }
  const obligTotal = obligations.reduce((s, o) => s + (parseFloat(o.installment) || 0), 0)
  obligBody.push([
    { text: 'รวม', style: 'td', colSpan: 2, bold: true }, '',
    { text: baht(obligTotal), style: 'td', bold: true },
    { text: 'บาท', style: 'td' }
  ])

  // history table (3 rows)
  const histBody = [
    [{ text: '#', style: 'th' }, { text: 'ระยะเวลา', style: 'th' }, { text: 'ไฟแนนซ์', style: 'th' }, { text: 'ที่ไหน', style: 'th' }]
  ]
  for (let i = 0; i < 3; i++) {
    const h = history[i] || {}
    histBody.push([
      { text: String(i + 1), style: 'td' },
      { text: v(h.period), style: 'td' },
      { text: v(h.finance), style: 'td' },
      { text: v(h.where), style: 'td' }
    ])
  }

  // followups table (6 rows)
  const fuBody = [
    [{ text: 'ครั้งที่', style: 'th' }, { text: 'วันที่', style: 'th' }, { text: 'บันทึก', style: 'th' }]
  ]
  for (let i = 0; i < 6; i++) {
    const f = followups[i] || {}
    fuBody.push([
      { text: String(i + 1), style: 'td' },
      { text: v(f.date), style: 'td' },
      { text: v(f.note), style: 'td' }
    ])
  }

  const tLayout = {
    hLineColor: () => '#bbbbbb',
    vLineColor: () => '#bbbbbb',
    paddingTop: () => 1,
    paddingBottom: () => 1
  }

  const docDefinition = {
    pageOrientation: 'portrait',
    pageSize: 'A4',
    pageMargins: [22, 18, 18, 18],
    content: [
      { text: 'โปรไฟล์เครดิตลูกค้า', style: 'h1' },
      { text: 'วันที่รับงาน: ' + v(p.received_date) + '     ช่องทางรับงาน: ' + v(p.channel), style: 'topRight' },

      section('ข้อมูลลูกค้า', [
        kv('ชื่อ-นามสกุล', custName, 'เบอร์โทร', v(c.customer_mobile)),
        kv('อายุ', v(c.customer_age) + ' ปี', 'เพศ', v(c.customer_gender)),
        kv('ที่อยู่', v(c.customer_address), 'ลักษณะที่พัก', v(c.customer_housing) + (c.customer_housing_other ? ' (' + c.customer_housing_other + ')' : ''))
      ]),

      section('ความต้องการรถ', [
        kv('รถที่สนใจ', v(p.vehicle_model_interest), 'ค่างวดที่ต้องการ', baht(p.desired_installment)),
        kv('งบดาวน์', baht(p.down_budget), '', '')
      ]),

      section('พนักงานบริษัท', [
        kv('พนักงานบริษัท', v(p.employer), 'อายุงาน', v(p.work_years)),
        kv('รายได้/เดือน', baht(p.employed_income_amt), 'หลักฐานรายได้', incomeProof),
        kv('ประกันสังคม', v(p.sso_status) + (p.sso_months ? ' (' + p.sso_months + ')' : ''), 'รับผ่านบัญชี', v(p.bank_account)),
        kv('กี่เดือน', v(p.sso_months), 'ใบขับขี่', v(p.has_license))
      ]),

      section('ธุรกิจส่วนตัว', [
        kv('อาชีพ', v(p.business), 'ทำมากี่ปี', v(p.business_years)),
        kv('รายได้ต่อเดือน', baht(p.business_income_amt), 'เดินบัญชี', v(p.statement)),
        kv('มีเอกสาร', v(p.business_docs), 'มีสัญญาเช่าซื้อ/ว่าจ้าง', v(p.has_lease_contract))
      ]),

      section('ข้อมูลเครดิต', [
        kvFull('สินเชื่อที่มีอยู่', loans.length ? loans.join(', ') : '-'),
        kv('สถานะการผ่อน', v(p.credit_status) + (p.credit_status_other ? ' (' + p.credit_status_other + ')' : ''), '', '')
      ]),

      {
        table: { widths: ['6%', '44%', '25%', '25%'], body: obligBody },
        layout: tLayout,
        margin: [0, 0, 0, 3]
      },

      {
        table: { widths: ['6%', '30%', '30%', '34%'], body: histBody },
        layout: tLayout,
        margin: [0, 0, 0, 3]
      },

      section('เช็คลิสต์ธนาคาร/ไฟแนนซ์ที่ยื่น', [
        kvFull('ที่ยื่น', bankText),
        kvFull('อื่นๆ', v(p.banks_other))
      ]),

      {
        table: { widths: ['10%', '20%', '70%'], body: fuBody },
        layout: tLayout
      }
    ],
    styles: {
      h1: { fontSize: 16, bold: true, alignment: 'center', margin: [0, 0, 0, 3] },
      topRight: { fontSize: 11, alignment: 'right', margin: [0, 0, 0, 4] },
      secTitle: { fontSize: 12, bold: true, fillColor: '#eef3f1' },
      lbl: { fontSize: 10, bold: true },
      val: { fontSize: 11 },
      th: { fontSize: 10, bold: true, alignment: 'center', fillColor: '#eeeeee' },
      td: { fontSize: 10 }
    },
    defaultStyle: { font: 'THSarabunNew', lineHeight: 1.05 }
  }

  pdfMake.createPdf(docDefinition).open()
}
